// Real connection/health indicators (RF-UI-03, RF-UI-15). Both read current evidence:
// the API health endpoint and the SSE stream state, each with its last update time.
import { useI18n } from "../i18n/index.tsx";
import { useRealtime } from "../realtime/RealtimeProvider.tsx";
import { Button } from "../components/Button.tsx";
import { RefreshIcon, WifiOffIcon } from "../components/icons.tsx";

export function HealthIndicator() {
  const { t, formatTime } = useI18n();
  const { health, healthError, healthCheckedAt } = useRealtime();
  const checked = healthCheckedAt ? formatTime(healthCheckedAt.toISOString()) : "—";
  let tone = "unknown";
  let label = t("conn.health.checking");
  if (healthError) {
    tone = "bad";
    label = t("conn.health.unreachable");
  } else if (health) {
    // `status` reflects the API and its database; other components are listed separately.
    tone = health.status === "ok" ? "ok" : "warn";
    label = health.status === "ok" ? t("conn.health.healthy") : t("conn.health.degraded");
  }
  const degraded = (health?.components ?? []).filter((c) => c.state !== "healthy");
  const title = degraded.length > 0 ? degraded.map((c) => `${c.name}: ${c.state}${c.detail ? ` — ${c.detail}` : ""}`).join("\n") : undefined;
  return (
    <span className={`conn conn--${tone}`} data-testid="health-indicator" title={title}>
      <span className="conn-dot" aria-hidden="true" />
      <strong>{label}</strong>
      {degraded.length > 0 ? (
        <span className="tone-warning" data-testid="health-components">
          · {t("conn.componentsAttention", { n: degraded.length, list: degraded.map((c) => c.name).join(", ") })}
        </span>
      ) : null}
      <span className="subtle">· {t("conn.checkedAt", { time: checked })}</span>
    </span>
  );
}

export function LiveIndicator() {
  const { t, formatTime } = useI18n();
  const { status, lastConfirmedAt, staleSince, reconnect } = useRealtime();
  const since = lastConfirmedAt ?? staleSince;
  const sinceText = since ? formatTime(since.toISOString()) : "—";
  if (status === "live") {
    return (
      <span className="conn conn--ok" data-testid="live-indicator" data-status="live">
        <span className="conn-dot" aria-hidden="true" />
        <strong>{t("conn.live")}</strong>
        <span className="subtle">· {t("conn.updatedAt", { time: sinceText })}</span>
      </span>
    );
  }
  if (status === "connecting") {
    return (
      <span className="conn conn--unknown" data-testid="live-indicator" data-status="connecting">
        <span className="conn-dot" aria-hidden="true" />
        <strong>{t("conn.connecting")}</strong>
      </span>
    );
  }
  return (
    <span className={`conn conn--warn`} data-testid="live-indicator" data-status={status}>
      <WifiOffIcon size={14} />
      <strong>{status === "offline" ? t("conn.offline") : t("conn.reconnecting")}</strong>
      <span className="subtle">· {since ? t("conn.staleSince", { time: sinceText }) : t("conn.noDataYet")}</span>
      <Button variant="ghost" size="sm" onClick={reconnect} icon={<RefreshIcon size={14} />} style={{ minHeight: 24, padding: "0 6px" }}>
        {t("conn.retryNow")}
      </Button>
    </span>
  );
}

/** Banner-level warning placed above data that may be stale (does not move focus). */
export function StaleDataNotice() {
  const { t, formatTime } = useI18n();
  const { status, lastConfirmedAt, staleSince } = useRealtime();
  if (status === "live" || status === "connecting") return null;
  const since = lastConfirmedAt ?? staleSince;
  return (
    <p className="stale-notice" data-testid="stale-notice">
      <WifiOffIcon size={14} />
      {since ? t("conn.staleNotice", { time: formatTime(since.toISOString()) }) : t("conn.staleNoticeNoTime")}
    </p>
  );
}
